// * Lambda Functions
import { LambdaClient, InvokeCommand } from '@aws-sdk/client-lambda'
import { toUtf8, fromUtf8 } from '@aws-sdk/util-utf8-browser'
import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity'
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity'

import { IDashboardState } from '../states'

type IUser = {
    email: string
    firstName: string
    lastName: string
    role: string
}

// ? dashboardMode: 3 types landing | change_dashboard | start_authoring
export const lambdaInvoke = async ({
    id_token,
    dashboardMode,
    dashboardId,
}: {
    id_token: string
    dashboardMode: string
    dashboardId: string
}) => {
    try {
        const loginProvider = `cognito-idp.${process.env.REACT_APP_AWS_COGNITO_REGION}.amazonaws.com/${process.env.REACT_APP_AWS_USER_POOLS_ID}`
        const data = await new LambdaClient({
            region: process.env.REACT_APP_AWS_COGNITO_REGION,
            credentials: fromCognitoIdentityPool({
                client: new CognitoIdentityClient({
                    region: process.env.REACT_APP_AWS_COGNITO_REGION,
                }),
                identityPoolId:
                    process.env.REACT_APP_AWS_IDENTITY_POOL_ID || '',
                logins: {
                    [loginProvider]: id_token,
                },
            }),
        }).send(
            new InvokeCommand({
                FunctionName: process.env.REACT_APP_AWS_LAMBDA_FUNCTION_NAME,
                InvocationType: 'RequestResponse',
                LogType: 'Tail',
                Payload: fromUtf8(
                    JSON.stringify({
                        id_token,
                        mode: dashboardMode,
                        input: dashboardId,
                    })
                ),
            })
        )

        if (data.Payload) {
            const parsedData = JSON.parse(toUtf8(data.Payload))
            console.log('Lambda response:', parsedData) // Debug log

            const user: IUser = JSON.parse(parsedData.user)
            const body = JSON.parse(parsedData.body)
            console.log('Parsed body:', body) // Debug log

            // Handle simple string response (legacy format)
            if (typeof body === 'string') {
                return {
                    user,
                    url: body,
                }
            }

            // Handle response with dashboardsUrls and qUrl
            if (body.dashboardsUrls && body.qUrl) {
                // Check if dashboardsUrls is a string (dashboard click case) or object (landing case)
                if (typeof body.dashboardsUrls === 'string') {
                    console.log('Dashboard click case - returning specific dashboard URL')
                    return {
                        user,
                        url: body.dashboardsUrls,  // Direct URL string for specific dashboard
                        qUrl: body.qUrl  // Include the Q&A URL
                    }
                } else if (body.dashboardsUrls.Dashboards && Array.isArray(body.dashboardsUrls.Dashboards)) {
                    console.log('Landing case - returning dashboard list with Q&A URL')
                    const arry = body.dashboardsUrls.Dashboards as Array<{
                        DashboardId: string
                        Name: string
                        url?: string
                    }>

                    if (arry.length > 0) {
                        const dashboards: IDashboardState[] = arry.map(
                            ({ DashboardId: id, Name: name }) => ({
                                id,
                                name,
                            })
                        )

                        // For landing case, use the first dashboard's URL if available
                        const url: string = arry[0].url || ''

                        console.log('Landing case - returning with qUrl:', body.qUrl)
                        return {
                            user,
                            dashboards,
                            url,
                            qUrl: body.qUrl  // Q&A URL is available in both cases
                        }
                    }
                }
            }

            // Handle legacy case - returns list of dashboards for sidebar (without qUrl)
            if (body.Dashboards && Array.isArray(body.Dashboards)) {
                console.log('Legacy landing case - returning dashboard list without Q&A URL')
                const arry = body.Dashboards as Array<{
                    DashboardId: string
                    Name: string
                    url?: string
                }>

                if (arry.length > 0) {
                    const dashboards: IDashboardState[] = arry.map(
                        ({ DashboardId: id, Name: name }) => ({
                            id,
                            name,
                        })
                    )

                    // For landing case, use the first dashboard's URL if available
                    const url: string = arry[0].url || ''

                    return {
                        user,
                        dashboards,
                        url,
                        // qUrl not available in legacy format
                    }
                }
            }

            // Handle case where only dashboardsUrls is present (no qUrl)
            if (body.dashboardsUrls && !body.qUrl) {
                console.log('Dashboard URL only case')
                return {
                    user,
                    url: body.dashboardsUrls,
                }
            }
        }

        throw new Error('No data')
    } catch (error) {
        console.error(error as Error)
    }
}
